(function () {
  'use strict';

  const RECENT_KEY = 'ws_recently_viewed';
  const RECENT_MAX = 8;
  const RECENT_SHOW = 4;

  const RELATED_SHOW = 4;
  const STOPWORD_THRESHOLD = 0.6;   // 60% — Tokens die in ≥ 60% des Pools vorkommen sind Stopwords
  const BIGRAM_BONUS = 2;           // Faktor für geteilte 2-Wort-Sequenzen

  const SESSION_SEED_KEY = 'ws_related_seed';

  const HEADINGS = {
    complementary: { h2: 'Ergänzende Produkte', sub: 'Passende Ergänzungen zu diesem Artikel' },
    related:       { h2: 'Ähnliche Artikel',    sub: 'Weitere Artikel, die Ihnen gefallen könnten' },
    recent:        { h2: 'Zuletzt angesehen',   sub: 'Ihre zuletzt betrachteten Produkte' },
  };

  // ── Recent-Storage ─────────────────────────────────────────────────────────
  function loadRecent() {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); }
    catch (_) { return []; }
  }
  function saveRecent(list) {
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX))); }
    catch (_) { /* Storage voll oder deaktiviert – ignorieren */ }
  }
  function pushRecent(handle) {
    if (!handle) return;
    const list = loadRecent().filter(h => h !== handle);
    list.unshift(handle);
    saveRecent(list);
  }

  async function fetchRecentCard(handle) {
    const url = `/products/${encodeURIComponent(handle)}?view=recently-viewed-card`;
    const res = await fetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    return res.text();
  }

  function extractCards(html) {
    if (!html) return [];
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(doc.querySelectorAll('.ws-card'));
  }

  function renderCardsIntoPanel(panelContent, cards) {
    panelContent.innerHTML = '';
    if (!cards || cards.length === 0) return 0;
    const grid = document.createElement('div');
    grid.className = 'ws-products__grid ws-pdp__related-grid';
    cards.forEach(c => grid.appendChild(c));
    panelContent.appendChild(grid);
    return cards.length;
  }

  function setEmptyMessage(panelContent, text) {
    panelContent.innerHTML = `<p class="ws-pdp__panel-empty">${text}</p>`;
  }

  // ── Session-stable Seed & Hash ─────────────────────────────────────────────
  function sessionSeed() {
    let s = null;
    try { s = sessionStorage.getItem(SESSION_SEED_KEY); } catch (_) {}
    if (!s) {
      s = String(Math.floor(Math.random() * 2147483647));
      try { sessionStorage.setItem(SESSION_SEED_KEY, s); } catch (_) {}
    }
    return s;
  }

  // djb2-Hash — deterministisch, schnell, für Tiebreak-Wert per Kandidat
  function hashStr(str) {
    let h = 5381;
    for (let i = 0; i < str.length; i++) {
      h = ((h << 5) + h) + str.charCodeAt(i);
      h = h | 0;   // zurück auf 32-bit
    }
    return Math.abs(h);
  }

  // ── Titel-Tokenisierung ────────────────────────────────────────────────────
  // Lowercase → Apostrophe/Interpunktion raus → an Whitespace + Bindestrichen splitten
  // → leere Tokens filtern. Reihenfolge bleibt erhalten (für Bigrams gebraucht).
  function tokenize(title) {
    if (!title) return [];
    return title
      .toLowerCase()
      .replace(/[‘’‚‛'`´]/g, '')       // Apostrophe (inkl. typographische)
      .replace(/[.,;:!?()\[\]{}"„"«»]/g, ' ')              // Interpunktion
      .replace(/[-–—_/\\]+/g, ' ')                          // Dashes/Slashes
      .split(/\s+/)
      .filter(t => t.length > 0);
  }

  // Auto-Stopwords: Tokens die in ≥ threshold Anteil der Pool-Titel vorkommen
  function computeStopwords(tokenLists, threshold) {
    const freq = new Map();
    const total = tokenLists.length;
    if (total === 0) return new Set();
    for (const tokens of tokenLists) {
      const uniq = new Set(tokens);
      for (const t of uniq) freq.set(t, (freq.get(t) || 0) + 1);
    }
    const stopwords = new Set();
    for (const [t, c] of freq) {
      if (c / total >= threshold) stopwords.add(t);
    }
    return stopwords;
  }

  function filterTokens(tokens, stopwords) {
    return tokens.filter(t => !stopwords.has(t));
  }

  // Bigrams als Set von "wort1\0wort2" aus gefilterter Token-Reihenfolge
  function bigramsOf(filteredTokens) {
    const bigrams = new Set();
    for (let i = 0; i < filteredTokens.length - 1; i++) {
      bigrams.add(filteredTokens[i] + ' ' + filteredTokens[i + 1]);
    }
    return bigrams;
  }

  // ── Tab-Management ─────────────────────────────────────────────────────────
  function activateTab(root, key) {
    root.querySelectorAll('[data-tab]').forEach(t => {
      const active = t.dataset.tab === key;
      t.classList.toggle('ws-pdp__tab--active', active);
      t.setAttribute('aria-selected', String(active));
      t.setAttribute('tabindex', active ? '0' : '-1');
    });
    root.querySelectorAll('[data-panel]').forEach(p => {
      p.hidden = p.dataset.panel !== key;
    });
    const h = HEADINGS[key];
    if (h) {
      const h2 = root.querySelector('.ws-pdp__related-heading');
      const sub = root.querySelector('.ws-pdp__related-sub');
      if (h2) h2.textContent = h.h2;
      if (sub) sub.innerHTML = `<span class="ws-pdp__related-dash">—</span> ${h.sub}`;
    }
  }

  function hideTab(root, key) {
    const t = root.querySelector(`[data-tab="${key}"]`);
    const p = root.querySelector(`[data-panel="${key}"]`);
    if (t) t.hidden = true;
    if (p) p.hidden = true;
  }

  // ── Related-Ranking ────────────────────────────────────────────────────────
  // Server liefert bis zu 50 Kandidaten server-gerendert. Wir rechnen den
  // Ähnlichkeits-Score und behalten die Top-4. Score = |unigram-overlap|
  // + BIGRAM_BONUS * |bigram-overlap|, jeweils nach Auto-Stopword-Filter.
  // Ties werden per session-stabilem Hash aufgebrochen.
  function rankRelated(root, container) {
    const cards = Array.from(container.querySelectorAll('.ws-card'));
    if (cards.length === 0) return 0;

    const seed = sessionSeed();

    // Kandidaten-Info sammeln
    const candidateInfo = cards.map(card => {
      const titleEl = card.querySelector('.ws-card__title');
      const title = titleEl ? titleEl.textContent.trim() : '';
      return {
        card,
        title,
        tokens: tokenize(title),
        tiebreak: hashStr(title + seed),
      };
    });

    const sourceTitle = root.dataset.productTitle || '';
    const sourceTokensRaw = tokenize(sourceTitle);

    let scored;
    if (sourceTokensRaw.length > 0 && candidateInfo.length > 1) {
      // Auto-Stopwords über den gesamten Kandidaten-Pool
      const stopwords = computeStopwords(
        candidateInfo.map(c => c.tokens),
        STOPWORD_THRESHOLD
      );

      const sourceFiltered = filterTokens(sourceTokensRaw, stopwords);
      const sourceTokenSet = new Set(sourceFiltered);
      const sourceBigrams = bigramsOf(sourceFiltered);

      scored = candidateInfo.map(c => {
        const filtered = filterTokens(c.tokens, stopwords);
        const candTokenSet = new Set(filtered);
        const candBigrams = bigramsOf(filtered);

        let uni = 0;
        for (const t of candTokenSet) if (sourceTokenSet.has(t)) uni++;
        let bi = 0;
        for (const b of candBigrams) if (sourceBigrams.has(b)) bi++;

        return { card: c.card, score: uni + BIGRAM_BONUS * bi, tiebreak: c.tiebreak };
      });
    } else {
      // Fallback: keine Source-Tokens verfügbar → reine session-stabile Random-Auswahl
      scored = candidateInfo.map(c => ({ card: c.card, score: 0, tiebreak: c.tiebreak }));
    }

    // Sortierung: Score absteigend, Tiebreak aufsteigend (deterministischer Pseudo-Random)
    scored.sort((a, b) => (b.score - a.score) || (a.tiebreak - b.tiebreak));

    // Top-4 behalten, Rest aus DOM entfernen, Reihenfolge im DOM angleichen
    const keep = scored.slice(0, RELATED_SHOW);
    const keepSet = new Set(keep.map(s => s.card));
    for (const card of cards) {
      if (!keepSet.has(card)) card.remove();
    }
    const grid = container.querySelector('.ws-pdp__related-grid');
    if (grid) {
      for (const s of keep) grid.appendChild(s.card);
    }

    return keep.length;
  }

  // ── Init ───────────────────────────────────────────────────────────────────
  async function init() {
    const root = document.querySelector('[data-pdp-related]');
    if (!root) return;

    const productHandle = root.dataset.productHandle;
    if (productHandle) pushRecent(productHandle);

    const contents = {
      complementary: root.querySelector('[data-panel="complementary"] [data-panel-content]'),
      related:       root.querySelector('[data-panel="related"] [data-panel-content]'),
      recent:        root.querySelector('[data-panel="recent"] [data-panel-content]'),
    };

    const counts = {
      complementary: contents.complementary ? contents.complementary.querySelectorAll('.ws-card').length : 0,
      related:       0,
      recent:        0,
    };

    // Related: Ranking gegen den Pool, Top-4 behalten
    if (contents.related) {
      counts.related = rankRelated(root, contents.related);
    }

    // Zuletzt angesehen: localStorage → per-Handle-Fetch → Recent-Panel
    const recentHandles = loadRecent().filter(h => h !== productHandle).slice(0, RECENT_SHOW);
    if (recentHandles.length === 0) {
      setEmptyMessage(contents.recent, 'Du hast noch keine Produkte angesehen.');
    } else {
      const htmls = await Promise.all(
        recentHandles.map(h => fetchRecentCard(h).catch(() => ''))
      );
      const cards = [];
      for (const h of htmls) {
        for (const c of extractCards(h)) cards.push(c);
      }
      if (cards.length > 0) {
        counts.recent = renderCardsIntoPanel(contents.recent, cards);
      } else {
        setEmptyMessage(contents.recent, 'Du hast noch keine Produkte angesehen.');
      }
    }

    // Auto-Hide leerer Tabs (Complementary + Related). Recent bleibt sichtbar mit Empty-Message.
    if (counts.complementary === 0) hideTab(root, 'complementary');
    if (counts.related === 0)       hideTab(root, 'related');

    // Tab-Click-Handler
    root.querySelectorAll('[data-tab]').forEach(tab => {
      tab.addEventListener('click', () => activateTab(root, tab.dataset.tab));
    });

    // Tastatur-Navigation Pfeiltasten
    const tablist = root.querySelector('[data-pdp-tablist]');
    if (tablist) {
      tablist.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        const tabs = Array.from(tablist.querySelectorAll('[data-tab]')).filter(t => !t.hidden);
        const idx = tabs.indexOf(document.activeElement);
        if (idx === -1) return;
        e.preventDefault();
        const next = e.key === 'ArrowRight' ? (idx + 1) % tabs.length : (idx - 1 + tabs.length) % tabs.length;
        tabs[next].focus();
        activateTab(root, tabs[next].dataset.tab);
      });
    }

    // Ersten nicht-leeren Tab aktivieren (Priorität: complementary > related > recent)
    const priority = ['complementary', 'related', 'recent'];
    for (const key of priority) {
      if (counts[key] === 0 && key !== 'recent') continue;
      activateTab(root, key);
      break;
    }

    root.dataset.jsReady = 'true';

    // Wishlist-Re-init für per-JS angehängte Recent-Cards
    document.dispatchEvent(new CustomEvent('ws:products-appended', { detail: { count: 0 } }));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
